import React, { useMemo } from 'react';
import { Box, Typography, Stack, Tooltip, IconButton, Alert } from '@mui/material';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { useInstatDesign } from './theme/instatDesign';
import { Card, Pill, SkillRadar } from './ui';
import {
  RADAR_CATEGORIES,
  aggregateByCategory,
  iconForSkill,
  categoryForSkill,
} from './instatSkillsMapping';
import { openSkillVideo, openStatVideo } from './instatVideo';

// ---------------------------------------------------------------------------
// Single skill row: icon · label · progress bar · raw value · play button

function SkillRow({ skill, playerId, playerName, playerContext }) {
  const d = useInstatDesign();
  const Icon = iconForSkill(skill.label);
  const raw = Number(skill.value);
  const value = Number.isFinite(raw) ? raw : 0;
  const barWidth = Math.max(0, Math.min(100, value));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 32px 32px',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: `${d.radius.sm}px`,
        transition: `background-color ${d.motion.fast}`,
        '&:hover': { bgcolor: d.accentSofter },
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: d.accentSoft,
          color: d.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 13 }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 12.5, fontWeight: 600, color: d.colors.text, lineHeight: 1.15 }}
        >
          {skill.label}
        </Typography>
        <Box
          sx={{
            mt: 0.4,
            height: 3,
            width: '100%',
            bgcolor: d.colors.divider,
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${barWidth}%`,
              background: d.accentGradient,
              borderRadius: 999,
              transition: `width ${d.motion.slow}`,
            }}
          />
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: d.colors.text,
          ...d.typo.tabular,
          textAlign: 'right',
        }}
      >
        {Math.round(value)}
      </Typography>

      <Tooltip title={`Watch ${skill.label} clips`} arrow>
        <IconButton
          size="small"
          onClick={() => openSkillVideo({ playerId, playerName, skill, playerContext })}
          sx={{ color: d.accent, p: 0.25 }}
        >
          <PlayCircleFilledRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ---------------------------------------------------------------------------

function StrengthBadge({ kind, label, value }) {
  const d = useInstatDesign();
  const isStrength = kind === 'strength';
  const color = isStrength ? d.colors.success : d.colors.warning;
  const softBg = isStrength ? d.colors.successSoft : d.colors.warningSoft;
  const Icon = isStrength ? EmojiEventsRoundedIcon : WarningAmberRoundedIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: `${d.radius.md}px`,
        bgcolor: softBg,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: d.colors.surface,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: d.colors.text, lineHeight: 1.2 }}>
          {isStrength ? 'Elite' : 'Area to improve'}
        </Typography>
        <Typography
          noWrap
          sx={{ fontSize: 12.5, color: d.colors.textSecondary, lineHeight: 1.3 }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color,
          ...d.typo.tabular,
        }}
      >
        {Math.round(value)}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// All skills, grouped by radar category, sorted by value desc within each group.

function AllSkillsPanel({ skills, playerId, playerName, playerContext }) {
  const d = useInstatDesign();

  const grouped = useMemo(() => {
    const buckets = {};
    for (const c of RADAR_CATEGORIES) buckets[c.key] = { cat: c, items: [] };
    for (const s of skills || []) {
      const cat = categoryForSkill(s.label);
      if (!buckets[cat]) continue;
      buckets[cat].items.push(s);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k].items.sort((a, b) => (b.value || 0) - (a.value || 0));
    }
    return buckets;
  }, [skills]);

  const nonEmpty = RADAR_CATEGORIES.filter((c) => grouped[c.key].items.length > 0);

  if (!skills?.length) {
    return (
      <Card>
        <Typography sx={{ ...d.typo.headline, mb: 1 }}>Skills</Typography>
        <Typography sx={{ ...d.typo.caption }}>No skill ratings available.</Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ ...d.typo.headline, color: d.colors.text }}>
            Skills
          </Typography>
          <Typography sx={{ ...d.typo.caption, color: d.colors.textSecondary }}>
            {skills.length} skills · tap{' '}
            <PlayCircleFilledRoundedIcon
              sx={{ fontSize: 13, verticalAlign: 'text-bottom', color: d.accent }}
            />{' '}
            for clips
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {nonEmpty.map((c) => {
          const CatIcon = c.icon;
          const items = grouped[c.key].items;
          return (
            <Box
              key={c.key}
              sx={{
                p: 1.25,
                borderRadius: `${d.radius.md}px`,
                bgcolor: d.colors.canvasAlt,
                border: `1px solid ${d.colors.divider}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, px: 0.75 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: d.colors.surface,
                    color: d.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: d.shadows.card,
                  }}
                >
                  <CatIcon sx={{ fontSize: 13 }} />
                </Box>
                <Typography
                  sx={{ ...d.typo.micro, color: d.colors.textSecondary, flex: 1 }}
                >
                  {c.label}
                </Typography>
                <Pill tone="neutral" size="sm">{items.length}</Pill>
              </Stack>
              <Box>
                {items.map((s) => (
                  <SkillRow
                    key={s.skill_id}
                    skill={s}
                    playerId={playerId}
                    playerName={playerName}
                    playerContext={playerContext}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}

// ===========================================================================
//  Main component
// ===========================================================================

export default function OverviewDashboard({
  playerId,
  playerName,
  skills = [],
  currStats,
  seasonLabel,
  matchIds = [],
  playerContext = null,
}) {
  const d = useInstatDesign();

  // Radar: 6-axis aggregate
  const radarAxes = useMemo(() => aggregateByCategory(skills), [skills]);

  const strengths = useMemo(
    () =>
      [...(skills || [])]
        .filter((s) => Number.isFinite(Number(s.value)))
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .filter((s) => (s.value || 0) >= 80)
        .slice(0, 3),
    [skills],
  );
  const weaknesses = useMemo(
    () =>
      [...(skills || [])]
        .filter((s) => Number.isFinite(Number(s.value)) && (s.value || 0) < 45)
        .sort((a, b) => (a.value || 0) - (b.value || 0))
        .slice(0, 2),
    [skills],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Row 1 — Radar + Strengths */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 1.4fr) 1fr' },
          gap: 2.5,
        }}
      >
        {/* Radar */}
        <Card>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ ...d.typo.headline, color: d.colors.text }}>Profile</Typography>
              <Typography sx={{ ...d.typo.caption, color: d.colors.textSecondary }}>
                Hudl ratings · {skills?.length || 0} skills aggregated
              </Typography>
            </Box>
            {seasonLabel && <Pill tone="accent" size="sm">{seasonLabel}</Pill>}
          </Stack>
          {skills?.length ? (
            <SkillRadar axes={radarAxes} size={340} />
          ) : (
            <Box sx={{ height: 340, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ ...d.typo.caption }}>No skill ratings available.</Typography>
            </Box>
          )}
        </Card>

        {/* Strengths / weaknesses */}
        <Card>
          <Typography sx={{ ...d.typo.headline, color: d.colors.text, mb: 1.25 }}>Standout</Typography>
          <Stack spacing={1}>
            {strengths.map((s) => (
              <StrengthBadge key={`str-${s.skill_id}`} kind="strength" label={s.label} value={s.value} />
            ))}
            {weaknesses.map((s) => (
              <StrengthBadge key={`wk-${s.skill_id}`} kind="weakness" label={s.label} value={s.value} />
            ))}
            {strengths.length === 0 && weaknesses.length === 0 && (
              <Alert
                severity="info"
                sx={{
                  bgcolor: d.colors.infoSoft,
                  color: d.colors.info,
                  border: 'none',
                  borderRadius: `${d.radius.md}px`,
                  fontSize: 13,
                }}
              >
                No standout skills — player profile is balanced.
              </Alert>
            )}
          </Stack>
        </Card>
      </Box>

      {/* Row 2 — All skills grouped by category */}
      <AllSkillsPanel
        skills={skills}
        playerId={playerId}
        playerName={playerName}
        playerContext={playerContext}
      />

      {/* Row 3 — Full season stats grid (clickable → Hudl playlists) */}
      {Array.isArray(currStats) && currStats.length > 0 && (
        <SeasonStatsGrid
          playerId={playerId}
          playerName={playerName}
          seasonLabel={seasonLabel}
          currStats={currStats}
          matchIds={matchIds}
          playerContext={playerContext}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Full season stats grid (the clickable "playlists by stat" area)

function SeasonStatsGrid({ playerId, playerName, currStats, seasonLabel, matchIds, playerContext }) {
  const d = useInstatDesign();
  const hasMatches = (matchIds?.length || 0) > 0;

  return (
    <Card>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ ...d.typo.headline, color: d.colors.text }}>Season stats</Typography>
          <Typography sx={{ ...d.typo.caption, color: d.colors.textSecondary }}>
            click a stat → Hudl playlist
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75}>
          {seasonLabel && <Pill tone="accent" size="sm">{seasonLabel}</Pill>}
          <Pill tone="neutral" size="sm">
            {matchIds?.length || 0} matches
          </Pill>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(4, 1fr)',
            md: 'repeat(6, 1fr)',
            lg: 'repeat(8, 1fr)',
          },
          gap: 1,
        }}
      >
        {currStats.map((s, i) => {
          const clickable = s.clickable && hasMatches;
          const handleClick = () => {
            if (!clickable) return;
            openStatVideo({ playerId, playerName, stat: s, matchIds, seasonLabel, playerContext });
          };
          return (
            <Box
              key={`${s.order}-${s.statparam}-${i}`}
              onClick={handleClick}
              sx={{
                position: 'relative',
                p: 1.25,
                borderRadius: `${d.radius.md}px`,
                bgcolor: clickable ? d.colors.canvasAlt : d.colors.surfaceAlt,
                border: `1px solid ${clickable ? d.colors.divider : 'transparent'}`,
                cursor: clickable ? 'pointer' : 'default',
                transition: `all ${d.motion.fast}`,
                '&:hover': clickable ? {
                  bgcolor: d.accentSofter,
                  borderColor: d.accentBorder,
                  transform: 'translateY(-1px)',
                } : {},
              }}
            >
              <Typography sx={{ ...d.typo.micro, color: d.colors.textSecondary, mb: 0.25, minHeight: 15 }}>
                {s.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  ...d.typo.tabular,
                  color: d.colors.text,
                  lineHeight: 1.1,
                }}
              >
                {s.value}
              </Typography>
              {clickable && (
                <PlayCircleFilledRoundedIcon
                  sx={{ position: 'absolute', top: 6, right: 6, fontSize: 14, color: d.accent, opacity: 0.7 }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}
